import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, MessageSquare, CheckCircle2, Compass } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Your FuturePath may be evolving" card.
 *
 * Renders when the backend detects that recent exploration is pointing
 * somewhere different from the student's current CDP direction. Students
 * choose Exploring / Considering / Update, Update writes a new
 * `cdp_versions` row and updates the primary career field.
 */
export default function EvolutionCard() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/nc/cdp/evolution`, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (dismissed || !data?.has_evolution_signal) return null;

  const { emerging, current_direction, current_cluster, response } = data;
  const alreadyResolved = response === 'exploring';
  if (alreadyResolved) return null;

  const respond = async (choice) => {
    setBusy(true);
    try {
      await axios.post(`${API}/nc/cdp/evolution/respond`, { choice }, getAuthHeader());
      if (choice === 'update') {
        toast.success('Your direction has been updated. Your counselor has been notified.');
      } else if (choice === 'considering') {
        toast.success('Flagged for your counselor, they may bring it up in your next meeting.');
      } else {
        toast.success('Got it, you\'re just exploring.');
      }
      setDismissed(true);
      window.dispatchEvent(new Event('cdp-updated'));
    } catch {
      toast.error('Could not save your choice, try again.');
    } finally {
      setBusy(false);
    }
  };

  const strengthColor = emerging.strength === 'strong'
    ? '#f472b6'
    : emerging.strength === 'considering'
      ? '#a78bfa'
      : '#38bdf8';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="cdp-evolution-card"
      className="relative overflow-hidden rounded-2xl border border-violet-400/30 bg-[#0a1729]/70 p-6 mb-6"
      style={{ boxShadow: '0 40px 100px -60px rgba(167,139,250,0.6)' }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.4), transparent 65%)' }} />
      <div aria-hidden className="pointer-events-none absolute -left-24 -bottom-32 h-64 w-64 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.35), transparent 65%)' }} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-300/40 bg-violet-400/10 px-2.5 py-0.5
            text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200">
            <Sparkles className="h-3 w-3" /> FuturePath Intelligence
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{ borderColor: `${strengthColor}66`, background: `${strengthColor}18`, color: strengthColor }}>
            {emerging.strength} signal
          </span>
        </div>

        <h2 className="font-outfit mt-3 text-2xl sm:text-3xl font-semibold text-white tracking-tight">
          Your FuturePath may be{' '}
          <span className="bg-gradient-to-r from-violet-300 via-pink-300 to-cyan-300 bg-clip-text text-transparent">
            evolving
          </span>
        </h2>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-600/40 bg-slate-900/40 p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">Current direction</p>
            <p className="mt-1 text-lg font-semibold text-white">{current_direction || 'Undecided'}</p>
            {current_cluster && (
              <p className="mt-0.5 text-xs text-slate-300">{current_cluster}</p>
            )}
          </div>
          <div className="rounded-xl border border-violet-400/40 bg-violet-500/10 p-4"
            style={{ boxShadow: '0 0 28px -12px rgba(167,139,250,0.6)' }}>
            <div className="flex items-center gap-1.5">
              <Compass className="h-3 w-3 text-violet-300" />
              <p className="text-[10px] uppercase tracking-wider text-violet-200">Emerging direction</p>
            </div>
            <p className="mt-1 text-lg font-semibold text-white">{emerging.career_interest}</p>
            {emerging.cluster && (
              <p className="mt-0.5 text-xs text-violet-200/80">{emerging.cluster}</p>
            )}
          </div>
        </div>

        {emerging.signals?.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-2">Why FuturePath noticed</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              Your recent activity shows increased interest in{' '}
              <span className="text-white">
                {emerging.signals.slice(0, 4).join(', ')}
              </span>.
            </p>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-white/10">
          <p className="text-sm text-slate-300 mb-3">Which is it?</p>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={() => respond('exploring')}
              data-testid="evolution-just-exploring"
              className="rounded-full px-4 py-2 text-sm border border-slate-600/60 text-slate-200 hover:border-cyan-300/50 hover:text-cyan-100 transition-colors"
            >
              I'm just exploring
            </button>
            <button
              disabled={busy}
              onClick={() => respond('considering')}
              data-testid="evolution-considering"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-violet-400/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20 transition-colors"
              style={{ boxShadow: '0 0 22px -12px rgba(167,139,250,0.7)' }}
            >
              <MessageSquare className="h-3.5 w-3.5" /> I'm seriously considering it
            </button>
            <button
              disabled={busy}
              onClick={() => respond('update')}
              data-testid="evolution-update"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
              style={{ boxShadow: '0 0 30px -8px rgba(34,211,238,0.6)' }}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Update my direction
            </button>
            <Link
              to="/app/nc-cdp"
              className="ml-auto inline-flex items-center gap-1 text-sm text-slate-300 hover:text-cyan-300 transition-colors"
              data-testid="evolution-see-plan"
            >
              See my full plan <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
